import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { CreativeType } from 'packages/types/creative';
import { getUser } from 'packages/store/user/user.selectors';
import { catchResponseError } from 'packages/helpers/helpers';
import useAccessRights from 'packages/hooks/useAccessRights';
import features from '~/configs/features';
import renditionsService, { type RenditionConfiguration } from 'packages/services/renditionsService';

type UseAvailableExportFormatsProps = {
    creativeType: CreativeType;
};

const useAvailableExportFormats = ({ creativeType }: UseAvailableExportFormatsProps) => {
    const { currentCompany } = useSelector(getUser);
    const { isPlatformAllowed } = useAccessRights();
    const [isLoadingExportFormats, setIsLoadingExportFormats] = useState(true);
    const [availableExportFormats, setAvailableExportFormats] = useState<RenditionConfiguration[]>([]);

    useEffect(() => {
        let isDisposed = false;

        if (!currentCompany || !['IMAGE', 'VIDEO'].includes(creativeType)) {
            setAvailableExportFormats([]);
            setIsLoadingExportFormats(false);

            return () => {
                isDisposed = true;
            };
        }

        setIsLoadingExportFormats(true);

        renditionsService
            .getRenditionConfigurations(currentCompany, creativeType as 'IMAGE' | 'VIDEO')
            .then(({ json, status }) => {
                if (isDisposed) {
                    return;
                }

                if (status !== 200) {
                    setAvailableExportFormats([]);
                    setIsLoadingExportFormats(false);

                    return;
                }

                let renditions = json || [];

                if (!isPlatformAllowed([features.PDF])) {
                    renditions = renditions.filter((rendition) => rendition.type !== 'PDF');
                }

                renditions = [...renditions].sort(
                    (a, b) => (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity),
                );

                setAvailableExportFormats(renditions);
                setIsLoadingExportFormats(false);
            })
            .catch((error) => {
                if (isDisposed) {
                    return;
                }

                catchResponseError(error);
                setAvailableExportFormats([]);
                setIsLoadingExportFormats(false);
            });

        return () => {
            isDisposed = true;
        };
    }, [currentCompany, creativeType, isPlatformAllowed]);

    return {
        isLoadingExportFormats,
        availableExportFormats,
    };
};

export default useAvailableExportFormats;
